<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="isOpen" class="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4">
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/70 backdrop-blur-sm" @click="$emit('close')" />

                <!-- Modal -->
                <div
                    ref="modalRef"
                    class="overflow-hidden relative w-full max-w-lg bg-zinc-950 rounded-t-3xl sm:rounded-2xl border-t border-white/10 sm:border border-white/10 shadow-2xl max-h-[90vh] flex flex-col"
                    :style="modalStyle"
                    @touchstart="handleTouchStart"
                    @touchmove="handleTouchMove"
                    @touchend="handleTouchEnd"
                >
                    <!-- Mobile Pull Handle -->
                    <div class="sm:hidden flex justify-center pt-2 pb-1 cursor-grab active:cursor-grabbing">
                        <div class="w-10 h-1 rounded-full bg-zinc-700" />
                    </div>

                    <!-- Header -->
                    <div class="px-5 pt-4 pb-3 border-b border-white/10">
                        <div class="flex items-center gap-2.5 mb-1.5">
                            <button
                                @click="$emit('close')"
                                class="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors shrink-0"
                                aria-label="Back"
                            >
                                <ArrowLeft class="w-4 h-4 text-white" />
                            </button>
                            <h2 class="text-xl font-bold text-white flex-1">{{ titleText }}</h2>
                            <button
                                @click="$emit('close')"
                                class="w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center transition-colors shrink-0"
                                aria-label="Close"
                            >
                                <X class="w-4 h-4 text-white/70" />
                            </button>
                        </div>
                        <p class="text-white/60 text-xs leading-snug pl-11">
                            Members who sponsored {{ audition.creator.name }}'s audition
                        </p>
                    </div>

                    <!-- Update Name Banner -->
                    <div v-if="currentUser && !currentUser.display_name" class="mx-5 mt-3">
                        <div class="bg-zinc-900 rounded-xl px-3 py-2.5 border border-white/10 flex items-center justify-between gap-2.5">
                            <p class="text-white/70 text-xs leading-snug">
                                Want your name to stand out?
                            </p>
                            <a :href="route('members2.account.profile')" class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors whitespace-nowrap">
                                Update
                            </a>
                        </div>
                    </div>

                    <!-- Scrollable Sponsors List -->
                    <div v-if="allSponsors.length === 0 && !isLoading" class="flex-1 flex items-center justify-center px-5 py-12">
                        <div class="text-center max-w-xs">
                            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/10 border border-blue-500/20 mb-4">
                                <img :src="STAR_ICON" alt="" class="w-8 h-8" />
                            </div>
                            <h3 class="text-white font-semibold text-base mb-2">
                                Be the First Sponsor
                            </h3>
                            <p class="text-white/60 text-sm leading-relaxed">
                                No one has sponsored this audition yet. Give stars now to claim
                                the #1 spot and help bring this fantasy to life!
                            </p>
                        </div>
                    </div>
                    <div
                        v-else
                        ref="scrollContainer"
                        class="flex-1 overflow-y-auto px-2 py-2 sm:max-h-[500px] max-h-[800px]"
                        @scroll="handleScroll"
                    >
                        <div>
                            <div v-for="(sponsor, index) in allSponsors" :key="sponsor.user_id">
                                <div
                                    @click="goToMyProfile(sponsor)"
                                    :class="[
                                        'rounded-2xl px-4 py-3 transition-colors flex items-center gap-3 cursor-pointer',
                                        sponsor.is_current_user ? 'bg-zinc-900 border border-white/10' : 'hover:bg-white/[0.04]'
                                    ]"
                                >
                                    <!-- Rank number -->
                                    <div class="w-6 shrink-0 text-right">
                                        <span
                                            :class="[
                                            'text-xs font-semibold',
                                            sponsor.rank === 1
                                                ? 'text-white'
                                                : isTop(sponsor.rank)
                                                ? 'text-white/65'
                                                : 'text-white/35'
                                          ]"
                                        >
                                          {{ sponsor.rank }}
                                        </span>
                                    </div>

                                    <!-- Avatar -->
                                    <div
                                        :class="[
                                            'w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0',
                                            getAvatarBg(sponsor.rank)
                                        ]"
                                    >
                                        {{ getInitial(sponsor.sponsor_name) }}
                                    </div>

                                    <!-- Username -->
                                    <div class="flex-1 min-w-0">
                                        <div class="flex items-center gap-1.5">
                                            <p class="text-white font-semibold text-sm truncate">
                                                {{ sponsor.sponsor_name }}
                                            </p>
                                            <span v-if="sponsor.has_generic_name" class="text-white/40 text-[10px]">
                                                (Generic)
                                            </span>
                                        </div>
                                        <a v-if="sponsor.is_current_user && sponsor.has_generic_name"
                                           :href="route('members2.account.profile')"
                                           class="text-blue-400 hover:text-blue-300 text-xs font-medium transition-colors"
                                        >
                                            Personalize →
                                        </a>
                                    </div>

                                    <!-- Stars -->
                                    <div class="flex items-center gap-1 shrink-0">
                                        <span class="text-white font-bold text-sm">
                                            {{ sponsor.total_stars }}
                                        </span>
                                        <img :src="STAR_ICON" alt="" class="w-4 h-4" />
                                    </div>
                                </div>

                                <!-- Comment -->
                                <div v-if="getComment(sponsor, index)" class="px-4 pb-2 -mt-1">
                                    <div class="ml-[3.75rem]">
                                        <div
                                            :class="[
                                                'rounded-lg px-3 py-2 border',
                                                !getCommentColors(sponsor, index) && 'bg-white/[0.04] border-white/[0.06]'
                                            ]"
                                            :style="getCommentColors(sponsor, index) ? {
                                                backgroundColor: getCommentColors(sponsor, index)[0],
                                                borderColor: getCommentColors(sponsor, index)[1],
                                                color: getCommentColors(sponsor, index)[2],
                                            } : {}"
                                        >
                                            <p :class="['text-xs leading-relaxed', getCommentColors(sponsor, index) ? 'opacity-90' : 'text-white/70']">"{{ getComment(sponsor, index).text }}"</p>
                                        </div>
                                        <div v-if="getComment(sponsor, index).reply" class="mt-1.5 ml-2 flex gap-2">
                                            <div
                                                class="w-0.5 self-stretch rounded-full shrink-0"
                                                :style="{ backgroundColor: getCommentColors(sponsor, index) ? getCommentColors(sponsor, index)[0] + '66' : 'rgba(59,130,246,0.4)' }"
                                            />
                                            <div class="py-0.5">
                                                <div class="flex items-center gap-1 mb-0.5">
                                                    <span class="text-blue-400 text-[10px] font-bold">{{ getComment(sponsor, index).reply.user_name }}</span>
                                                    <span class="text-white/30 text-[10px]">replied</span>
                                                </div>
                                                <p class="text-white/60 text-xs leading-relaxed">"{{ getComment(sponsor, index).reply.text }}"</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="mx-6 h-px bg-white/8" />
                            </div>

                            <!-- Loading with Skeleton -->
                            <div v-if="isLoading" class="space-y-0">
                                <div v-for="i in 3" :key="`skeleton-${i}`" class="animate-pulse">
                                    <div class="rounded-2xl px-4 py-3 flex items-center gap-3">
                                        <!-- Rank skeleton -->
                                        <div class="w-6 h-4 bg-white/10 rounded shrink-0"></div>

                                        <!-- Avatar skeleton -->
                                        <div class="w-8 h-8 rounded-full bg-white/10 shrink-0"></div>

                                        <!-- Name skeleton -->
                                        <div class="flex-1">
                                            <div class="h-4 bg-white/10 rounded w-24"></div>
                                        </div>

                                        <!-- Stars skeleton -->
                                        <div class="flex items-center gap-1">
                                            <div class="h-4 w-8 bg-white/10 rounded"></div>
                                            <div class="w-4 h-4 bg-white/10 rounded-full"></div>
                                        </div>
                                    </div>
                                    <div class="mx-6 h-px bg-white/8" />
                                </div>
                            </div>

                            <!-- End of list -->
                            <div v-if="!hasMorePages && allSponsors.length > 0" class="text-center py-4">
                                <p class="text-white/40 text-xs">
                                    That's everyone!
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Footer CTA -->
                    <div class="px-5 py-4 border-t border-white/10 bg-zinc-950">
                        <button
                            @click="$emit('giveStars')"
                            class="w-full py-2.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold text-sm rounded-lg transition-all active:scale-95"
                        >
                            Leave a comment for {{ audition.creator.name }}
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import {computed, ref, watch} from 'vue'
import { ArrowLeft, X } from 'lucide-vue-next'
import {router, usePage} from "@inertiajs/vue3";
import axios from 'axios'

const STAR_ICON = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const getComment = (sponsor) => {
    return sponsor.comment || null
}

const getCommentColors = (sponsor, index) => {
    if (sponsor.comment) {
        return getSuperChatColors(sponsor.comment.stars_given)
    }
    return getSuperChatColors(sponsor.total_stars)
}

const getSuperChatColors = (stars) => {
    if (stars >= 600) return ['#E62117', '#D00000', '#FFFFFF']
    if (stars >= 320) return ['#F57C00', '#E65100', '#FFFFFF']
    if (stars >= 160) return ['#FFCA28', '#FFB300', '#000000']
    if (stars >= 80) return ['#1DE9B6', '#00BFA5', '#000000']
    if (stars >= 40) return ['#00E5FF', '#00B8D4', '#000000']
    if (stars >= 25) return ['#1565C0', '#1565C0', '#FFFFFF']
    return null
}

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    audition: {
        type: Object,
        default: null
    },
    sponsors: {
        type: Object,
        required: true
    },
    isUserLoggedIn: {
        type: Boolean,
        required: true
    },
    routePrefix: {
        type: String,
        required: true
    }
})

const emit = defineEmits(['close', 'giveStars'])

// Infinite scroll state
const allSponsors = ref([])
const currentPage = ref(1)
const isLoading = ref(false)
const hasMorePages = ref(true)
const scrollContainer = ref(null)

const titleText = computed(() => {
    const total = props.sponsors.total || 0
    return total === 0 ? 'Sponsors' : `Sponsors (${total})`
})

const currentUser = usePage().props?.auth?.user

// Initialize with first page data
watch(() => props.sponsors, (newSponsors) => {
    if (newSponsors && newSponsors.data) {
        allSponsors.value = [...newSponsors.data]
        currentPage.value = newSponsors.current_page
        hasMorePages.value = newSponsors.current_page < newSponsors.last_page
    }
}, { immediate: true })

// Reset when modal opens
watch(() => props.isOpen, (isOpen) => {
    if (isOpen) {
        // Reset to initial state
        allSponsors.value = [...props.sponsors.data]
        currentPage.value = props.sponsors.current_page
        hasMorePages.value = props.sponsors.current_page < props.sponsors.last_page

        // Reset scroll position
        if (scrollContainer.value) {
            scrollContainer.value.scrollTop = 0
        }
    }
})

// Load more sponsors
const loadMoreSponsors = async () => {
    if (isLoading.value || !hasMorePages.value) return

    isLoading.value = true

    try {
        const nextPage = currentPage.value + 1
        const response = await axios.get(route('members2.reels.top-sponsors', {
            post: props.audition.id,
            page: nextPage,
            per_page: 20
        }))

        const newData = response.data

        // Append new sponsors to the list
        allSponsors.value = [...allSponsors.value, ...newData.data]
        currentPage.value = newData.current_page
        hasMorePages.value = newData.current_page < newData.last_page

    } catch (error) {
        console.error('Failed to load more sponsors:', error)
    } finally {
        isLoading.value = false
    }
}

// Detect when user scrolls near bottom
const handleScroll = (e) => {
    if (isDragging.value) return // Don't trigger while dragging modal

    const container = e.target
    const scrollTop = container.scrollTop
    const scrollHeight = container.scrollHeight
    const clientHeight = container.clientHeight

    // Load more when 200px from bottom
    const threshold = 200
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight

    if (distanceFromBottom < threshold && hasMorePages.value && !isLoading.value) {
        loadMoreSponsors()
    }
}

// Helper functions
const isTop = (rank) => rank <= 3

const getInitial = (username) => username.charAt(0).toUpperCase()

const getAvatarBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-br from-amber-400 to-amber-600'
    if (rank === 2) return 'bg-gradient-to-br from-zinc-300 to-zinc-500'
    if (rank === 3) return 'bg-gradient-to-br from-amber-600 to-amber-800'
    return 'bg-blue-600'
}

// Touch/swipe handling
const modalRef = ref(null)
const touchStartY = ref(0)
const touchCurrentY = ref(0)
const isDragging = ref(false)

const modalStyle = computed(() => {
    if (!isDragging.value) return {}

    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY < 0) return {} // Don't allow swipe up

    return {
        transform: `translateY(${deltaY}px)`,
        transition: 'none'
    }
})

const handleTouchStart = (e) => {
    // Only handle touches on the handle area (top 60px)
    const touch = e.touches[0]
    const rect = modalRef.value.getBoundingClientRect()

    if (touch.clientY - rect.top > 60) return // Not in handle area

    touchStartY.value = touch.clientY
    touchCurrentY.value = touch.clientY
    isDragging.value = true
}

const handleTouchMove = (e) => {
    if (!isDragging.value) return

    const touch = e.touches[0]
    touchCurrentY.value = touch.clientY

    const deltaY = touchCurrentY.value - touchStartY.value

    // Prevent scrolling when dragging down
    if (deltaY > 0) {
        e.preventDefault()
    }
}

const handleTouchEnd = () => {
    if (!isDragging.value) return

    const deltaY = touchCurrentY.value - touchStartY.value
    const threshold = 100 // Close if dragged more than 100px

    if (deltaY > threshold) {
        emit('close')
    }

    isDragging.value = false
    touchStartY.value = 0
    touchCurrentY.value = 0
}

const goToMyProfile = (entry) => {
    const url = props.isUserLoggedIn ?
        route('members2.profile.my', { slug: entry.sponsor_name }) :
        route(props.routePrefix + '.profile.my', { slug: entry.sponsor_name })

    router.visit(url, {
        preserveScroll: false,
        preserveState: true,
    })
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}
</style>
